/* eslint-disable react/no-unescaped-entities */

import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { FormProvider } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { setDraftCount, setHasPostings } from '~/actions/PermanentJobs/PermanentJobs';
import { Box, Button, Center, Column, CustomSpinner, Row, Text } from '~/commonComponents';
import { EnforcePostAJobFunnelImprovements } from '~/constants/ff.posthog';
import { useCreateDraftJobPost, useSaveJobPostDetail } from '~/domain/PermanentJobs';
import { useAppSelector } from '~/hooks';
import { showErrorMessage } from '~/domain/utils';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useUserSelector } from '~/hooks/useUserSelector';
import { useGetProfessionalCountsByOffice } from '~/domain/Professionals/usecases/useGetProfessionalCountsByOffice';
import { firePermPostJobBasicInfoSubmitted } from '~/posthogs/events';
import { Colors } from '~/themes/colors';
import {
  AutoSaveStatus,
  BasicInfoForm,
  GeneralErrorMessage,
  InfoSection,
  JobDetailsFormMapper,
  useJobDetailsForm,
} from '../JobDetailsForm';
import {
  useAutoSaveDraftJobPost,
  useAutoScrollToSection,
  useDraftJobPost,
  useSaveJobPost,
} from './hooks';
import {
  CreatePermanentJobPostingFormSection,
  CreatePermanentJobPostingSteps,
  TCreatePermanentJobPostingPageRoutes,
} from './Types';

export const BasicInformationPage = () => {
  const { id } = useParams<TCreatePermanentJobPostingPageRoutes>();
  const isCreating = !id;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const draftCount = useAppSelector((state) => state.permanentJobs.draftCount);

  const { draft, isLoading } = useDraftJobPost(id);
  const isEnforcePostAJobFunnelImprovement = useFeatureFlag(EnforcePostAJobFunnelImprovements);

  const user = useUserSelector();
  const { totalCount } = useGetProfessionalCountsByOffice(user?.id, {
    enable: isEnforcePostAJobFunnelImprovement,
    distance: 25,
  });

  const generalJobInfoRef = useRef<HTMLDivElement>(null);

  const controller = useJobDetailsForm({
    initialValues: draft,
  });

  useEffect(() => {
    if (draft) {
      controller.reset(JobDetailsFormMapper.fromEntity(draft));
    }
  }, [draft, controller]);

  useAutoScrollToSection({
    sectionRefs: {
      [CreatePermanentJobPostingFormSection.GeneralJobInformation]: generalJobInfoRef,
    },
    isReady: !isLoading && !!draft,
  });

  const { isPending, hasFormErrors, onSave } = useSaveJobPost(controller, id, {
    targetStep: CreatePermanentJobPostingSteps.WorkDetails,
    triggers: ['jobTitle', 'office', 'officeSpecialty', 'professionType'],
    onTrackSuccess: (postingId, formData) => {
      firePermPostJobBasicInfoSubmitted({
        postingId,
        professionType: formData.professionType,
        professionalCount: totalCount,
      });
    },
  });

  const {
    isLoading: isAutoSaveLoading,
    isShowSavedMessage,
    lastSavedAt,
  } = useAutoSaveDraftJobPost(controller, id);

  const createDraftJobPost = useCreateDraftJobPost();
  const saveJobPostDetail = useSaveJobPostDetail();
  const isCreatingPending = createDraftJobPost.isPending || saveJobPostDetail.isPending;

  const onSaveAndCreateDraft = useCallback(async () => {
    const isValid = await controller.trigger(['professionType', 'jobTitle', 'officeSpecialty']);

    if (!isValid) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    createDraftJobPost.mutate(undefined, {
      onSuccess: (data) => {
        if (!data?.id) return;

        const fullPayload = JobDetailsFormMapper.toSaveJobPostDetailPayload(controller.getValues());
        const payload = {
          title: fullPayload.title,
          professionType: fullPayload.professionType,
          subProfession: fullPayload.subProfession,
          officeSpecialty: fullPayload.officeSpecialty,
        };
        saveJobPostDetail.mutate(
          { id: data.id, payload },
          {
            onSuccess: () => {
              dispatch(setHasPostings(true));
              dispatch(setDraftCount((draftCount ?? 0) + 1));
              firePermPostJobBasicInfoSubmitted({
                postingId: data.id,
                professionType: controller.getValues().professionType,
                professionalCount: totalCount,
              });
              const step = CreatePermanentJobPostingSteps.WorkDetails;
              navigate(
                `/permanent-jobs/postings/${data.id}/create/${step}`,
              );
            },
            onError: (err) => showErrorMessage({ error: err }),
          },
        );
      },
      onError: (err) => showErrorMessage({ error: err }),
    });
  }, [controller, createDraftJobPost, saveJobPostDetail, dispatch, navigate, totalCount, draftCount]);

  const handleSave = isCreating ? onSaveAndCreateDraft : onSave;
  const handleIsPending = isCreating ? isCreatingPending : isPending || isAutoSaveLoading;

  if (isLoading) {
    return (
      <Center marginBlock={48}>
        <CustomSpinner isLoading size={24} />
      </Center>
    );
  }

  if (isEnforcePostAJobFunnelImprovement) {
    return (
      <>
        {!isCreating && <AutoSaveStatus visible={isShowSavedMessage} date={lastSavedAt} />}
        <Column alignItems="center" gap={16}>
          <Column alignItems="center" gap={10}>
            <Text bold fontSize={32} color={Colors.neutral_600} textAlign="center">
              Let's See Who's In Your Area
            </Text>
            <Text fontSize={16} color={Colors.neutral_500} textAlign="center" maxWidth={633}>
              Tell us what you're hiring for and we'll show you how many professionals are near you.
            </Text>
          </Column>
          {hasFormErrors && <GeneralErrorMessage />}
          <Box width={800}>
            <FormProvider {...controller}>
              <InfoSection
                ref={generalJobInfoRef}
                title="Job Information"
                titleFontSize={24}
                titleColor="neutral_600"
              >
                <BasicInfoForm />
              </InfoSection>
            </FormProvider>
          </Box>
          <Row justifyContent="center" marginTop={24}>
            <Button
              text="Find Out Now"
              onClick={handleSave}
              isLoading={handleIsPending}
            />
          </Row>
        </Column>
      </>
    );
  }

  return (
    <>
      {!isCreating && <AutoSaveStatus visible={isShowSavedMessage} date={lastSavedAt} />}
      <Column width="60%" alignSelf="center">
        <Text bold fontSize={22} marginBottom={14} marginLeft={24}>
          Step 1: Basic Information
        </Text>
        {hasFormErrors && <GeneralErrorMessage />}
        <FormProvider {...controller}>
          <InfoSection
            ref={generalJobInfoRef}
            icon="briefcase-solid"
            title="General Job Information"
          >
            <BasicInfoForm />
          </InfoSection>
        </FormProvider>
        <Row justifyContent="center" marginTop={24}>
          <Button text="Next" onClick={handleSave} isLoading={handleIsPending} />
        </Row>
      </Column>
    </>
  );
};
